import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import sortBy from 'lodash/sortBy'
import { createSelector } from 'reselect'
import createDeepEqualSelector from '@signal/utils/createDeepEqualSelector'
import select from '@signal/utils/select'
import {
  cookActiveAccountForSelectors,
  cookPermissionsForSelectors
} from '../auth/selectors'
import AccountPermissionsContext from '../permissions/Context'
import {
  ActiveAccountContext,
  UserAccountsContext,
  UserDetailsContext
} from './Context'
import { CONTAINER_KEYS, CONTAINER_TO_GROUP } from './constants'

export const rootSelector = state => get(state, 'user')

export const featureFlagsSelector = createSelector(
  rootSelector,
  select('featureFlags')
)

export const getFeatureFlagSelector = featureKey =>
  createSelector(featureFlagsSelector, select(featureKey, false))

/**
 * User Details Selectors
 */

export const detailsRootSelector = createDeepEqualSelector(rootSelector, user =>
  get(user, 'me')
)

export const userIdSelector = createSelector(detailsRootSelector, details =>
  get(details, 'id')
)

export const firstNameSelector = createSelector(detailsRootSelector, details =>
  get(details, 'firstName')
)

export const lastNameSelector = createSelector(detailsRootSelector, details =>
  get(details, 'lastName')
)

export const nameSelector = createSelector(
  firstNameSelector,
  lastNameSelector,
  (firstName, lastName) => {
    return [firstName, lastName].filter(Boolean).join(' ')
  }
)

export const initialsSelector = createSelector(
  firstNameSelector,
  lastNameSelector,
  (firstName, lastName) => {
    return [firstName, lastName]
      .filter(Boolean)
      .map(name => name[0])
      .join('')
  }
)

export const emailSelector = createSelector(detailsRootSelector, details =>
  get(details, 'email')
)

/**
 * User Accounts Selectors
 */

export const accountsRootSelector = createDeepEqualSelector(
  rootSelector,
  user => get(user, 'accounts')
)

export const accountsListSelector = createSelector(
  accountsRootSelector,
  accounts => get(accounts, 'list')
)

export const accountsByIdSelector = createSelector(
  accountsRootSelector,
  accounts => get(accounts, 'byId')
)

export const accountsSelector = createSelector(
  accountsListSelector,
  accountsByIdSelector,
  (accountsList, accountsById) => {
    if (!accountsList) return null
    const accounts = accountsList.map(id => get(accountsById, id))
    return sortBy(accounts, 'name')
  }
)

/**
 * functions to create simulated redux state for data obtained by context fetcher for use with selectors
 */

export function cookUserAccountForSelector(account) {
  account = cloneDeep(account)
  CONTAINER_KEYS.forEach(containerKey => {
    const container = account[containerKey]
    if (container) {
      const groupKey = CONTAINER_TO_GROUP[container.level]
      if (groupKey) {
        account[groupKey] = {
          ...container,
          level: groupKey
        }
      }
      delete account[containerKey]
    }
  })
  return account
}

export function cookUserAccountsForSelectors({ response }) {
  if (!response) {
    return {
      user: {
        accounts: {
          loading: true
        }
      }
    }
  }
  const cookedAccounts = response.map(cookUserAccountForSelector)
  return {
    user: {
      accounts: {
        byId: cookedAccounts.reduce(
          (byId, { accountId, accountName, ...rest }) => {
            byId[accountId] = {
              accountId,
              id: accountId,
              name: accountName,
              // accountName prop is expected from picker and it was being omitted
              accountName,
              ...rest
            }
            return byId
          },
          {}
        ),
        list: cookedAccounts.map(({ accountId }) => accountId),
        loading: false
      }
    }
  }
}

export function cookUserDetailsForSelectors({ response }) {
  if (!response) {
    return {
      user: {
        loading: true
      }
    }
  }
  return {
    user: {
      loading: false,
      me: response
    }
  }
}

// standard account cookers for use with useContextsInReduxForm
export function getReduxUserAndAccountCookers() {
  return [
    {
      context: ActiveAccountContext,
      cookers: [cookActiveAccountForSelectors]
    },
    {
      context: AccountPermissionsContext,
      cookers: [cookPermissionsForSelectors]
    },
    {
      context: UserAccountsContext,
      cookers: [cookUserAccountsForSelectors]
    },
    {
      context: UserDetailsContext,
      cookers: [cookUserDetailsForSelectors]
    }
  ]
}
